const SVG_STYLE_PROPERTIES = Object.freeze([
  'color', 'display', 'visibility', 'opacity',
  'fill', 'fill-opacity', 'fill-rule',
  'stroke', 'stroke-opacity', 'stroke-width', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin',
  'marker-start', 'marker-mid', 'marker-end',
  'font-family', 'font-size', 'font-style', 'font-weight', 'letter-spacing',
  'text-anchor', 'dominant-baseline', 'paint-order', 'shape-rendering', 'rx', 'ry'
]);

const normalizeStyleValue = (property, value) => {
  if (!property.startsWith('marker-')) return value;
  const fragment = value.match(/#([^)'"\s]+)/)?.[1];
  return fragment ? `url(#${fragment})` : value;
};

const dependency = (provided, fallback, name) => {
  const value = provided ?? fallback;
  if (!value) throw new Error(`PNG_EXPORT_UNAVAILABLE: ${name}`);
  return value;
};

export function serializeSvgWithComputedStyles(svg, options = {}) {
  if (!svg?.cloneNode || !svg?.querySelectorAll) throw new Error('PNG_EXPORT_INVALID_SVG');
  const getStyle = dependency(options.getComputedStyle, globalThis.getComputedStyle, 'getComputedStyle');
  const Serializer = dependency(options.XMLSerializerCtor, globalThis.XMLSerializer, 'XMLSerializer');
  const clone = svg.cloneNode(true);
  const sourceNodes = [svg, ...svg.querySelectorAll('*')];
  const clonedNodes = [clone, ...clone.querySelectorAll('*')];
  if (sourceNodes.length !== clonedNodes.length) throw new Error('PNG_EXPORT_SVG_CLONE_MISMATCH');

  sourceNodes.forEach((source, index) => {
    const computed = getStyle(source);
    const declarations = SVG_STYLE_PROPERTIES.map((property) => {
      const value = computed?.getPropertyValue ? computed.getPropertyValue(property) : computed?.[property];
      return value ? `${property}:${normalizeStyleValue(property, value)}` : '';
    }).filter(Boolean).join(';');
    if (declarations) clonedNodes[index].setAttribute('style', declarations);
  });
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(options.width || 1920));
  clone.setAttribute('height', String(options.height || 710));
  return new Serializer().serializeToString(clone);
}

const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
  try {
    canvas.toBlob((blob) => {
      if (!blob || blob.size === 0) reject(new Error('PNG_EXPORT_EMPTY_BLOB'));
      else resolve(blob);
    }, 'image/png');
  } catch (error) {
    reject(new Error(`PNG_EXPORT_BLOB_FAILED: ${error.message}`));
  }
});

const loadImage = (ImageCtor, source) => new Promise((resolve, reject) => {
  const image = new ImageCtor();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('PNG_EXPORT_IMAGE_LOAD_FAILED'));
  image.src = source;
});

export async function exportSvgAsPng(svg, options = {}) {
  const documentRef = dependency(options.documentRef, globalThis.document, 'document');
  const URLRef = dependency(options.URLRef, globalThis.URL, 'URL');
  const BlobCtor = dependency(options.BlobCtor, globalThis.Blob, 'Blob');
  const ImageCtor = dependency(options.ImageCtor, globalThis.Image, 'Image');
  const scheduleCleanup = options.scheduleCleanup || globalThis.setTimeout;
  const width = options.width || 1920;
  const height = options.height || 710;
  const serialized = serializeSvgWithComputedStyles(svg, { ...options, width, height });
  if (!serialized) throw new Error('PNG_EXPORT_EMPTY_SVG');

  const svgUrl = URLRef.createObjectURL(new BlobCtor([serialized], { type: 'image/svg+xml;charset=utf-8' }));
  let image;
  try {
    image = await loadImage(ImageCtor, svgUrl);
  } finally {
    URLRef.revokeObjectURL(svgUrl);
  }

  const canvas = documentRef.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext?.('2d');
  if (!context) throw new Error('PNG_EXPORT_CANVAS_CONTEXT_UNAVAILABLE');
  context.fillStyle = options.background || '#f9faf6';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  const png = await canvasToBlob(canvas);

  const downloadUrl = URLRef.createObjectURL(png);
  let anchor;
  try {
    anchor = documentRef.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = options.filename || 'experience-entry-gate.png';
    anchor.hidden = true;
    documentRef.body?.appendChild(anchor);
    if (typeof anchor.click !== 'function') throw new Error('download click unavailable');
    anchor.click();
  } catch (error) {
    URLRef.revokeObjectURL(downloadUrl);
    throw new Error(`PNG_EXPORT_DOWNLOAD_FAILED: ${error.message}`);
  } finally {
    anchor?.remove?.();
  }
  if (scheduleCleanup) scheduleCleanup(() => URLRef.revokeObjectURL(downloadUrl), 0);
  else URLRef.revokeObjectURL(downloadUrl);
  return png;
}
